import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Globe, Instagram, Facebook, ArrowRight, Search, ChevronDown, X, Check,
  Utensils, Shirt, Gem, Home as HomeIcon, Sparkles, Palette, Baby,
  BookOpen, Cpu, Wrench, Tag, LayoutGrid,
} from "lucide-react";
import "./VendorsDirectory.css";

const CATEGORY_OPTIONS = [
  "Food & Beverage", "Fashion & Apparel", "Jewelry & Accessories",
  "Home & Decor", "Beauty & Wellness", "Art & Crafts",
  "Kids & Toys", "Books & Stationery", "Tech & Gadgets", "Services",
];

const CATEGORY_ICONS = {
  "Food & Beverage": Utensils,
  "Fashion & Apparel": Shirt,
  "Jewelry & Accessories": Gem,
  "Home & Decor": HomeIcon,
  "Beauty & Wellness": Sparkles,
  "Art & Crafts": Palette,
  "Kids & Toys": Baby,
  "Books & Stationery": BookOpen,
  "Tech & Gadgets": Cpu,
  "Services": Wrench,
};

const iconForCategory = (cat) => CATEGORY_ICONS[cat] || Tag;

// Search terms tuned for nicer Unsplash results than the raw category label.
const CATEGORY_SEARCH_TERMS = {
  "Food & Beverage": "gourmet food market",
  "Fashion & Apparel": "boutique clothing rack",
  "Jewelry & Accessories": "handmade jewelry",
  "Home & Decor": "home decor interior",
  "Beauty & Wellness": "skincare beauty products",
  "Art & Crafts": "art supplies craft",
  "Kids & Toys": "colorful kids toys",
  "Books & Stationery": "stationery notebooks desk",
  "Tech & Gadgets": "tech gadgets minimal",
  "Services": "small business workshop",
};

// Reads a Vite (VITE_UNSPLASH_ACCESS_KEY) or CRA (REACT_APP_UNSPLASH_ACCESS_KEY)
// env var without throwing in whichever bundler isn't in use.
const UNSPLASH_ACCESS_KEY =
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_UNSPLASH_ACCESS_KEY) ||
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_UNSPLASH_ACCESS_KEY) ||
  "";

const PAGE_SIZE = 12;

// A small fixed palette of tile tints, assigned deterministically per vendor
// so the grid feels varied even when a vendor has no logo uploaded yet.
const TILE_TINTS = ["tint-a", "tint-b", "tint-c", "tint-d", "tint-e", "tint-f"];

const hashOf = (id = "") => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return hash;
};

const tintForVendor = (id = "") => TILE_TINTS[hashOf(id) % TILE_TINTS.length];

const VendorsDirectory = () => {
  const location = useLocation();

  const [events, setEvents]                 = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventsLoading, setEventsLoading]    = useState(true);

  // "all" shows every approved vendor across every event; "event" scopes to one.
  const [viewMode, setViewMode] = useState("all");

  const [vendors, setVendors]           = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [error, setError]               = useState("");

  const [activeCategory, setActiveCategory] = useState(null);
  const [searchInput, setSearchInput]       = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage]                     = useState(1);

  const gridTopRef = useRef(null);

  useEffect(() => {
    const loadEvents = async () => {
      setEventsLoading(true);
      const { data, error: evErr } = await supabase
        .from("events")
        .select("id, name, start_date, status")
        .neq("status", "draft")
        .order("start_date", { ascending: false });

      if (evErr) {
        console.error("Failed to load events:", evErr.message);
        setEvents([]);
      } else {
        setEvents(data || []);
        if (data?.length) {
          const globalVillage = data.find((ev) =>
            ev.name?.toLowerCase().includes("global village")
          );
          setSelectedEventId((prev) => prev ?? (globalVillage?.id ?? data[0].id));
        }
      }
      setEventsLoading(false);
    };
    loadEvents();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Merges a list of approved applications into deduped vendor profiles,
  // unioning categories in case a vendor shows up more than once (multiple events).
  const mergeApplicationsToVendors = useCallback(async (applications) => {
    if (!applications || applications.length === 0) {
      setVendors([]);
      setVendorsLoading(false);
      return;
    }

    const categoriesByVendor = {};
    applications.forEach((app) => {
      const cats = (app.categories || [])
        .flat(Infinity)
        .filter((c) => typeof c === "string" && c.trim().length > 0);
      const existing = categoriesByVendor[app.vendor_id] || [];
      categoriesByVendor[app.vendor_id] = [...new Set([...existing, ...cats])];
    });

    const vendorIds = Object.keys(categoriesByVendor);

    const { data: profiles, error: profileErr } = await supabase
      .from("public_vendor_profiles")
      .select("*")
      .in("id", vendorIds);

    if (profileErr) {
      console.error("Failed to load vendor profiles:", profileErr.message);
      setError("Couldn't load vendors.");
      setVendors([]);
    } else {
      const merged = (profiles || []).map((p) => ({
        ...p,
        categories: categoriesByVendor[p.id] || [],
      }));
      setVendors(merged);
    }
    setVendorsLoading(false);
  }, []);

  const loadVendorsForEvent = useCallback(async (eventId) => {
    if (!eventId) return;
    setVendorsLoading(true);
    setError("");

    const { data: applications, error: appErr } = await supabase
      .from("vendor_applications")
      .select("vendor_id, categories")
      .eq("event_id", eventId)
      .eq("status", "approved");

    if (appErr) {
      console.error("Failed to load applications:", appErr.message);
      setError("Couldn't load vendors for this event.");
      setVendors([]);
      setVendorsLoading(false);
      return;
    }
    mergeApplicationsToVendors(applications);
  }, [mergeApplicationsToVendors]);

  const loadAllVendors = useCallback(async () => {
    setVendorsLoading(true);
    setError("");

    const { data: applications, error: appErr } = await supabase
      .from("vendor_applications")
      .select("vendor_id, categories")
      .eq("status", "approved");

    if (appErr) {
      console.error("Failed to load applications:", appErr.message);
      setError("Couldn't load vendors.");
      setVendors([]);
      setVendorsLoading(false);
      return;
    }
    mergeApplicationsToVendors(applications);
  }, [mergeApplicationsToVendors]);

  useEffect(() => {
    if (viewMode === "all") {
      loadAllVendors();
    } else if (selectedEventId) {
      loadVendorsForEvent(selectedEventId);
    }
  }, [viewMode, selectedEventId, loadAllVendors, loadVendorsForEvent]);

  const filteredVendors = useMemo(() => {
    return vendors.filter((v) => {
      if (activeCategory && !v.categories?.includes(activeCategory)) return false;
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const haystack = `${v.brand_name || ""} ${(v.categories || []).join(" ")}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [vendors, activeCategory, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [viewMode, selectedEventId, activeCategory, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / PAGE_SIZE));
  const paginatedVendors = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredVendors.slice(start, start + PAGE_SIZE);
  }, [filteredVendors, page]);

  const availableCategories = useMemo(() => {
    const present = new Set();
    vendors.forEach((v) => (v.categories || []).forEach((c) => c && present.add(c)));
    const known = CATEGORY_OPTIONS.filter((c) => present.has(c));
    const custom = [...present].filter((c) => !CATEGORY_OPTIONS.includes(c)).sort();
    return [...known, ...custom];
  }, [vendors]);

  // Unsplash photo per category, keyed by category name. Populated by the
  // effect below; falls back to the line-icon tile when a key isn't
  // configured, a category has no match yet, or the request fails.
  const [categoryPhotos, setCategoryPhotos] = useState({});

  useEffect(() => {
    if (!UNSPLASH_ACCESS_KEY || availableCategories.length === 0) return;

    const toFetch = availableCategories.filter((cat) => !categoryPhotos[cat]);
    if (toFetch.length === 0) return;

    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        toFetch.map(async (cat) => {
          const query = CATEGORY_SEARCH_TERMS[cat] || cat;
          try {
            const res = await fetch(
              `https://api.unsplash.com/search/photos?per_page=1&orientation=squarish&query=${encodeURIComponent(query)}`,
              { headers: { Authorization: `Client-ID ${UNSPLASH_ACCESS_KEY}` } }
            );
            if (!res.ok) throw new Error(`Unsplash ${res.status}`);
            const data = await res.json();
            const photo = data?.results?.[0];
            if (!photo) return [cat, null];
            return [cat, {
              url: photo.urls?.small,
              photographer: photo.user?.name,
              photographerUrl: photo.user?.links?.html,
            }];
          } catch (e) {
            console.error("Unsplash fetch failed for category:", cat, e);
            return [cat, null];
          }
        })
      );
      if (!cancelled) {
        setCategoryPhotos((prev) => {
          const next = { ...prev };
          entries.forEach(([cat, photo]) => { if (photo) next[cat] = photo; });
          return next;
        });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCategories]);

  const categoryTiles = useMemo(() => {
    return availableCategories.map((cat) => ({
      name: cat,
      photo: categoryPhotos[cat] || null,
    }));
  }, [availableCategories, categoryPhotos]);

  const hasCategoryPhotos = Object.keys(categoryPhotos).length > 0;

  // Backdrop mosaic for the hero — a handful of real vendor photos, tone-mapped
  // so the collage reads as one consistent editorial image rather than a grid of logos.
  const mosaicImages = useMemo(
    () => vendors.map((v) => v.logo_url).filter(Boolean).slice(0, 18),
    [vendors]
  );

  const goToPage = (updaterOrValue) => {
    setPage(updaterOrValue);
    requestAnimationFrame(() => {
      gridTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const selectedEvent = events.find((ev) => ev.id === selectedEventId);
  const hasActiveFilters = Boolean(activeCategory || debouncedSearch);

  const clearFilters = () => {
    setActiveCategory(null);
    setSearchInput("");
  };

  const heroSub = vendorsLoading
    ? "Gathering vendors…"
    : viewMode === "all"
      ? `${vendors.length} independent ${vendors.length === 1 ? "business" : "businesses"} across all our events.`
      : `${vendors.length} ${vendors.length === 1 ? "business" : "businesses"} joining ${selectedEvent?.name || "this event"}.`;

  return (
    <div className="vd-page">
      <section className={`vd-hero ${mosaicImages.length < 6 ? "vd-hero--fallback" : ""}`}>
        <div className="vd-hero-media" aria-hidden="true">
          {mosaicImages.length >= 6 && (
            <div className="vd-hero-mosaic">
              {mosaicImages.map((src, i) => (
                <div key={i} className="vd-hero-tile" style={{ backgroundImage: `url(${src})` }} />
              ))}
            </div>
          )}
          <div className="vd-hero-scrim" />
        </div>

        <div className="vd-hero-content">
          <h1 className="vd-hero-title">Discover independent makers.</h1>
          <p className="vd-hero-sub">{heroSub}</p>

          <div className="vd-mode-row">
            <div className="vd-mode-toggle" role="tablist" aria-label="Browse vendors">
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "all"}
                className={viewMode === "all" ? "vd-mode-btn vd-mode-btn--active" : "vd-mode-btn"}
                onClick={() => setViewMode("all")}
              >
                All vendors
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "event"}
                className={viewMode === "event" ? "vd-mode-btn vd-mode-btn--active" : "vd-mode-btn"}
                onClick={() => setViewMode("event")}
              >
                By event
              </button>
            </div>

            {viewMode === "event" && (
              <div className="vd-event-picker">
                <select
                  id="vd-event-select"
                  className="vd-event-select"
                  value={selectedEventId || ""}
                  onChange={(e) => setSelectedEventId(e.target.value)}
                  disabled={eventsLoading || events.length === 0}
                  aria-label="Choose an event"
                >
                  {eventsLoading && <option>Loading…</option>}
                  {!eventsLoading && events.length === 0 && <option>No events found</option>}
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.name}</option>
                  ))}
                </select>
                <span aria-hidden="true">{eventsLoading ? "Loading…" : (selectedEvent?.name || "Select event")}</span>
                <ChevronDown size={16} aria-hidden="true" />
              </div>
            )}
          </div>

          <div className="vd-search">
            <Search size={16} strokeWidth={2} />
            <input
              type="text"
              placeholder="Search vendors or categories…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                type="button"
                className="vd-search-clear"
                onClick={() => setSearchInput("")}
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      </section>

      {categoryTiles.length > 0 && (
        <section className="vd-category-section">
          <h2 className="vd-section-heading">Shop by category</h2>
          <div className="vd-category-grid">
            <button
              type="button"
              className={`vd-cat-card ${activeCategory === null ? "vd-cat-card--active" : ""}`}
              onClick={() => setActiveCategory(null)}
            >
              <span className="vd-cat-media vd-cat-media--icon">
                <LayoutGrid size={22} />
              </span>
              <span className="vd-cat-label">All</span>
              {activeCategory === null && <span className="vd-cat-check"><Check size={12} /></span>}
            </button>

            {categoryTiles.map(({ name, photo }) => {
              const Icon = iconForCategory(name);
              const active = activeCategory === name;
              return (
                <button
                  type="button"
                  key={name}
                  className={`vd-cat-card ${active ? "vd-cat-card--active" : ""}`}
                  onClick={() => setActiveCategory(active ? null : name)}
                >
                  {photo?.url ? (
                    <span className="vd-cat-media">
                      <img
                        src={photo.url}
                        alt=""
                        loading="lazy"
                        title={photo.photographer ? `Photo by ${photo.photographer} on Unsplash` : undefined}
                      />
                    </span>
                  ) : (
                    <span className="vd-cat-media vd-cat-media--icon">
                      <Icon size={22} />
                    </span>
                  )}
                  <span className="vd-cat-label">{name}</span>
                  {active && <span className="vd-cat-check"><Check size={12} /></span>}
                </button>
              );
            })}
          </div>

          {hasCategoryPhotos && (
            <p className="vd-cat-credit">
              Category photos via{" "}
              <a
                href="https://unsplash.com/?utm_source=your_app_name&utm_medium=referral"
                target="_blank"
                rel="noopener noreferrer"
              >
                Unsplash
              </a>
            </p>
          )}
        </section>
      )}

      <div ref={gridTopRef} className="vd-grid-top-anchor" />

      {error && <p className="vd-error">{error}</p>}

      {vendorsLoading ? (
        <div className="vd-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="vd-card vd-card--skeleton" />
          ))}
        </div>
      ) : filteredVendors.length === 0 ? (
        <div className="vd-empty">
          <p className="vd-empty-title">Nothing matches yet</p>
          <p className="vd-empty-sub">
            {hasActiveFilters
              ? "Try a different search term or category."
              : "No approved vendors here yet — check back soon."}
          </p>
          {hasActiveFilters && (
            <button type="button" className="vd-empty-clear" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="vd-results-row">
            <span>
              Showing {paginatedVendors.length} of {filteredVendors.length}{" "}
              {filteredVendors.length === 1 ? "vendor" : "vendors"}
            </span>
            {hasActiveFilters && (
              <button type="button" className="vd-results-clear" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          <div className="vd-grid">
            {paginatedVendors.map((vendor) => (
              <VendorCard key={vendor.id} vendor={vendor} backgroundLocation={location} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="vd-pagination">
              <button
                type="button"
                className="vd-page-btn"
                onClick={() => goToPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </button>
              <span className="vd-page-status">Page {page} of {totalPages}</span>
              <button
                type="button"
                className="vd-page-btn"
                onClick={() => goToPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const EMPTY_VALUES = ["na", "n/a", "none", "-", ""];

const isEmptyLink = (raw) => !raw || EMPTY_VALUES.includes(raw.trim().toLowerCase());

const toWebsiteUrl = (raw) => {
  if (isEmptyLink(raw)) return null;
  const val = raw.trim();
  if (/^https?:\/\//i.test(val)) return val;
  return `https://${val.replace(/^\/+/, "")}`;
};

const toInstagramUrl = (raw) => {
  if (isEmptyLink(raw)) return null;
  const val = raw.trim();
  if (/^https?:\/\//i.test(val)) return val;
  const handle = val.replace(/^@/, "");
  return `https://instagram.com/${handle}`;
};

const toFacebookUrl = (raw) => {
  if (isEmptyLink(raw)) return null;
  const val = raw.trim();
  if (/^https?:\/\//i.test(val)) return val;
  return `https://facebook.com/${val.replace(/^\/+/, "")}`;
};

const VendorCard = ({ vendor, backgroundLocation }) => {
  const websiteUrl   = toWebsiteUrl(vendor.website);
  const instagramUrl = toInstagramUrl(vendor.instagram);
  const facebookUrl  = toFacebookUrl(vendor.facebook);
  const tint = tintForVendor(vendor.id || vendor.brand_name || "");

  return (
    <Link
      to={`/v/${vendor.slug}`}
      state={{ backgroundLocation }}
      className="vd-card"
    >
      <div className={`vd-card-tile ${tint}`}>
        {vendor.logo_url
          ? <img src={vendor.logo_url} alt="" loading="lazy" />
          : <span className="vd-card-tile-letter">{vendor.brand_name?.[0]?.toUpperCase() || "?"}</span>}
      </div>

      <div className="vd-card-body">
        <h3 className="vd-card-name">{vendor.brand_name}</h3>

        {vendor.categories?.length > 0 && (
          <div className="vd-card-chip-row">
            {vendor.categories.slice(0, 2).map((cat) => (
              <span key={cat} className="vd-card-chip">{cat}</span>
            ))}
          </div>
        )}

        <div className="vd-card-footer">
          <div className="vd-card-socials">
            {websiteUrl && (
              <span className="vd-card-social-icon" aria-label="Has website">
                <Globe size={14} />
              </span>
            )}
            {instagramUrl && (
              <span className="vd-card-social-icon" aria-label="Has Instagram">
                <Instagram size={14} />
              </span>
            )}
            {facebookUrl && (
              <span className="vd-card-social-icon" aria-label="Has Facebook">
                <Facebook size={14} />
              </span>
            )}
          </div>
          <span className="vd-card-view">
            View <ArrowRight size={13} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default VendorsDirectory;